import { cn } from '@/utils/utils.ts';
import { useGetPlantOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import { useGetPlantDevicesOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-devices-overview.ts';
import { useGetPlantDevicesQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-devices-plant.ts';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { getTodayDateRange } from '@/utils/get-today-date-range.ts';
import { usePermissions } from '@/hooks/use-permission.ts';
import { DeviceTreeTableCard } from '@/features/devices/components/device-tree-table/device-tree-table-card.tsx';
import { MultiDeviceMetricsTableCard } from '@/features/devices/components/multi-device-metrics-table/multi-device-metrics-table-card.tsx';
import { PlantEvents } from '@/features/plants/components/plant-events/plant-events.tsx';
import { PlantProductionAndStorageTodayMetrics } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-today-metrics.tsx';
import { PlantProductionAndStorageEnergyManagement } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-energy-management.tsx';
import { PlantProductionOnlyFlowAndDetailsCard } from '@/features/plants/components/plant-overview/production-only/components/plant-production-only-flow-and-details-card.tsx';

import style from './plant-production-only-overview.module.scss';

const EMPTY_DEVICES: never[] = [];

const PlantProductionOnlyOverview = ({
  plantId,
  isLoading: isPlantLoading,
}: {
  plantId: string | undefined;
  isLoading?: boolean;
}) => {
  const [canEditProductionFields] = usePermissions<'plants'>([
    ['plants', 'edit.plant.production-fields'],
  ]);

  const { data: plant } = useGetPlantQuery({
    plantId: plantId,
  });

  const { data: overview, isLoading: isPlantOverviewLoading } = useGetPlantOverviewQuery({
    plantId: plantId,
  });

  const { data: plantDevices, isLoading: isDevicesLoading } = useGetPlantDevicesQuery({
    plantId: plantId ?? '',
    enabled: Boolean(plantId),
  });

  const { data: devicesOverview, isLoading: isDevicesOverviewLoading } =
    useGetPlantDevicesOverviewQuery({
      plantId: plantId,
      enabled: Boolean(plantId),
      dateRange: getTodayDateRange(),
    });

  const isLoading = isPlantLoading || isPlantOverviewLoading;

  return (
    <div className={cn(style.grid_container)}>
      <div className={cn(style.grid, 'gap-6')}>
        <PlantProductionAndStorageTodayMetrics
          overview={overview}
          isLoading={isLoading}
          className={style.today_metrics}
        />

        <PlantProductionOnlyFlowAndDetailsCard
          overview={overview}
          plant={plant}
          isLoading={isLoading}
          className={cn(style.realtime_metrics, 'min-h-[490px]')}
        />

        <PlantProductionAndStorageEnergyManagement
          isLoading={isLoading}
          plantId={plantId}
          className={cn(style.energy_management_chart, 'min-h-[440px] sm:min-h-[535px]')}
        />

        <DeviceTreeTableCard
          title="Device"
          data={plantDevices ?? EMPTY_DEVICES}
          isLoading={isLoading || isDevicesLoading}
          className={cn(style.devices)}
        />

        <MultiDeviceMetricsTableCard
          data={devicesOverview?.production}
          config={devicesOverview?.config.production}
          type={'production'}
          isLoading={isLoading || isDevicesOverviewLoading}
          plantId={plantId || ''}
          title="Overview Production"
          className={style.production}
          showSelectFields={canEditProductionFields}
        />

        <PlantEvents className={style.events} />
      </div>
    </div>
  );
};

export default PlantProductionOnlyOverview;
